import React, { useState } from 'react';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { FormControlLabel, Stack, Switch } from '@mui/material';

import { TooltipIconButton } from '@paragon/client-tools';
import { BasicProfile, ProfileLine, WebJointSegment } from '@paragon/contract-library';
import { degreesToSlope, distanceEquals, slopeToDegrees } from '@paragon/geometry-library';
import { AngleField, DistanceField, NumericField } from '@paragon/material';

import {
  calculateNumberOfFloatingJoints,
  calculateProfileLineGeometry,
  calculateWebJointSegmentGeometry,
  findProfileLineForWebJointSegment,
  isFullProfile,
} from '../../../../../../library/profiles/profileManipulation';
import {
  updateDistanceAndSlopeForProfileLine,
  updateNumberOfPanelsOnWebJointSegment,
} from '../../../../../../store/actions/profileActions';
import { getSingleSelectedItemEntryOrNull } from '../../../../../../store/selectors/getSelectedItemSelector';
import { getInsertingOrRewebbingProfile } from '../../../../../../store/selectors/profileSelectors';
import { useAppDispatch, useAppSelector } from '../../../../../hooks';

/**
 * Returns `null` if number of panels cannot be modified
 */
export function calculateNumberOfPanels(
  profile: BasicProfile,
  profileLine: ProfileLine,
  webJointSegment: WebJointSegment,
): number | null {
  if (profileLine.profileLineType !== 'TopChord' && profileLine.profileLineType !== 'BottomChord') return null;

  const { basePoint, endPoint } = calculateWebJointSegmentGeometry(profile, profileLine, webJointSegment);
  const isVertical = distanceEquals(basePoint.x, endPoint.x);
  if (isVertical) return null;

  if (webJointSegment.numberOfFloatingJoints != null) return webJointSegment.numberOfFloatingJoints + 1;

  return calculateNumberOfFloatingJoints(profile, profileLine.profileLineType, Math.abs(endPoint.x - basePoint.x)) + 1;
}

const WebJointSegmentEditor = () => {
  const dispatch = useAppDispatch();

  const [editingBaseJoint, setEditingBaseJoint] = useState(false);

  const profile = useAppSelector(getInsertingOrRewebbingProfile);
  const selectedEntry = useAppSelector(getSingleSelectedItemEntryOrNull);
  if (selectedEntry?.type !== 'webJointSegment') return null;

  const webJointSegment = selectedEntry.item;
  const profileLine = findProfileLineForWebJointSegment(profile, webJointSegment);
  const profileLineGeometry = calculateProfileLineGeometry(profile, profileLine);
  const isRewebbing = !isFullProfile(profile);

  const editingBaseJointChange = (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) =>
    setEditingBaseJoint(checked);

  const setDistanceAndSlope = async (distance: number, slope: number) =>
    await dispatch(updateDistanceAndSlopeForProfileLine(profileLine, distance, slope, !editingBaseJoint));

  const setNumberOfPanels = async (numberOfPanels: number | null) =>
    await dispatch(updateNumberOfPanelsOnWebJointSegment(webJointSegment, numberOfPanels));

  const { distance, slope } = (() => {
    const { basePoint, endPoint } = profileLineGeometry;
    const y = endPoint.y - basePoint.y;
    const x = endPoint.x - basePoint.x;
    const isVertical = distanceEquals(basePoint.x, endPoint.x);
    const distance = Math.abs(isVertical ? y : x);
    const slope = isVertical ? (endPoint.y > basePoint.y ? 90 : -90) : 12 * (y / x);
    return { distance, slope };
  })();

  const panels = calculateNumberOfPanels(profile, profileLine, webJointSegment);

  return (
    <Stack spacing={2}>
      {isRewebbing ? null : (
        <FormControlLabel
          labelPlacement="end"
          control={<Switch checked={editingBaseJoint} onChange={editingBaseJointChange} value="line" />}
          label={!editingBaseJoint ? 'Joint Locked' : 'Joint Unlocked'}
        />
      )}

      <DistanceField
        label="Distance"
        value={distance}
        onSubmit={(distance) => setDistanceAndSlope(distance, slope)}
        disabled={isRewebbing}
      />

      <AngleField
        label="Slope"
        value={slopeToDegrees(slope)}
        defaultDisplayFormat="slope"
        onSubmit={(slopeInDegrees) => setDistanceAndSlope(distance, degreesToSlope(slopeInDegrees))}
        disabled={isRewebbing}
      />

      <NumericField
        label="Panels"
        value={panels ?? 1}
        disabled={panels == null}
        onSubmit={setNumberOfPanels}
        integers
        min={1}
        endAdornment={
          <TooltipIconButton
            title="Use max panel length"
            disabled={webJointSegment.numberOfFloatingJoints == null}
            size="small"
            onClick={() => setNumberOfPanels(null)}
          >
            <RestartAltIcon />
          </TooltipIconButton>
        }
      />
    </Stack>
  );
};

export default WebJointSegmentEditor;
